/**
 * Analytics Page Component
 * Purpose: Display detailed click analytics for a specific URL
 * Author: Traklet App
 * Created: 2025
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  TablePagination,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  ContentCopy,
  Refresh,
  TrendingUp,
  Mouse,
  Public,
  Computer,
  CalendarToday,
  TrendingUp as EnhancedIcon,
  Public as GeographicIcon,
  Devices as DevicesIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { analyticsApi, apiUtils } from '../utils/api';
import type { UrlAnalytics, ClickData } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Analytics: React.FC = () => {
  const { urlId } = useParams<{ urlId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [analytics, setAnalytics] = useState<UrlAnalytics | null>(null);
  const [clickDetails, setClickDetails] = useState<ClickData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination for click details
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Date filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  /**
   * Fetch analytics data
   */
  const fetchAnalytics = async (includeDetails = true) => {
    if (!urlId) {
      setError('Invalid URL ID');
      return;
    }
    
    try {
      setIsLoading(true);
      
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        include_details: includeDetails,
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      };
      
      const response = await analyticsApi.getClickAnalytics(parseInt(urlId), params);
      
      if (response.success && response.data) {
        setAnalytics(response.data);
        if (response.data.click_details) {
          setClickDetails(response.data.click_details);
        }
      } else {
        setError(response.message || 'Failed to fetch analytics');
      }
    } catch (error) {
      setError(apiUtils.getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Copy URL to clipboard
   */
  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('URL copied to clipboard!');
    } catch (error) {
      setError('Failed to copy URL');
    }
  };

  /**
   * Handle page change
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /**
   * Handle rows per page change
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Apply date filters
   */
  const handleApplyFilters = () => {
    setPage(0);
    fetchAnalytics();
  };

  /**
   * Clear date filters
   */
  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setPage(0);
    fetchAnalytics();
  };

  /**
   * Format user agent for display
   */
  const formatUserAgent = (userAgent: string): string => {
    // Simple browser detection
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Other';
  };

  /**
   * Get browser icon color
   */
  const getBrowserColor = (browser: string): string => {
    switch (browser) {
      case 'Chrome': return '#4285f4';
      case 'Firefox': return '#ff7139';
      case 'Safari': return '#1b88ca';
      case 'Edge': return '#0078d4';
      default: return '#666666';
    }
  };

  // Load analytics on component mount and when dependencies change
  useEffect(() => {
    fetchAnalytics();
  }, [urlId, page, rowsPerPage]);

  if (isLoading && !analytics) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  if (error && !analytics) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 }
      }}
    >
      {/* Header */}
      <Box mb={{ xs: 3, sm: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/dashboard')}
          sx={{ 
            mb: { xs: 2, sm: 3 },
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Back to Dashboard
        </Button>
        
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={{ xs: 2, sm: 0 }}
          mb={{ xs: 2, sm: 3 }}
        >
          <Box>
            <Typography 
              variant={isMobile ? "h4" : "h3"} 
              component="h1" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                mb: 0.5
              }}
            >
              URL Analytics
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontWeight: 400 }}
            >
              Detailed insights and performance metrics
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<EnhancedIcon />}
            onClick={() => navigate(`/enhanced-analytics/${urlId}`)}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: { xs: 2, sm: 3 }
            }}
          >
            Enhanced Analytics
          </Button>
        </Stack>
        
        {/* URL Info */}
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 2, sm: 3 }, 
            mb: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Stack spacing={{ xs: 2, sm: 3 }}>
            <Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontWeight: 500 }}
              >
                Short URL
              </Typography>
              <Stack 
                direction="row" 
                alignItems="center" 
                spacing={1}
                sx={{ flexWrap: 'wrap' }}
              >
                <Typography
                  variant={isMobile ? "body1" : "h6"}
                  component="a"
                  href={analytics.url.short_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': { textDecoration: 'underline' },
                    wordBreak: 'break-all'
                  }}
                >
                  {analytics.url.short_url}
                </Typography>
                <Tooltip title="Copy URL">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyUrl(analytics.url.short_url)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.contrastText'
                      }
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            
            <Divider />
            
            <Box>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                gutterBottom
                sx={{ fontWeight: 500 }}
              >
                Original URL
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  wordBreak: 'break-all',
                  color: 'text.primary',
                  lineHeight: 1.5
                }}
                title={analytics.url.long_url}
              >
                {analytics.url.long_url}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>

      {/* Enhanced Analytics Preview */}
      {analytics.enhanced_stats && (
        <>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mb: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              justifyContent="space-between" 
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={{ xs: 2, sm: 0 }}
              mb={{ xs: 2, sm: 3 }}
            >
              <Box display="flex" alignItems="center">
                <EnhancedIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  component="h2" 
                  sx={{ fontWeight: 600, color: 'text.primary' }}
                >
                  Enhanced Analytics Preview
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                onClick={() => navigate(`/enhanced-analytics/${urlId}`)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                View Details
              </Button>
            </Stack>
            
            {analytics.enhanced_stats.enhanced_available ? (
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      textAlign: 'center', 
                      p: { xs: 2, sm: 3 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  >
                    <GeographicIcon 
                      sx={{ 
                        fontSize: { xs: 32, sm: 40 }, 
                        color: 'primary.main', 
                        mb: 1 
                      }} 
                    />
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {analytics.enhanced_stats.geographic.unique_countries}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontWeight: 500, mb: 1 }}
                    >
                      Countries
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                    >
                      Top: {analytics.enhanced_stats.geographic.top_country || 'N/A'}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      textAlign: 'center', 
                      p: { xs: 2, sm: 3 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  >
                    <DevicesIcon 
                      sx={{ 
                        fontSize: { xs: 32, sm: 40 }, 
                        color: 'primary.main', 
                        mb: 1 
                      }} 
                    />
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {analytics.enhanced_stats.devices.top_device || 'N/A'}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontWeight: 500, mb: 1 }}
                    >
                      Top Device
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                    >
                      {analytics.enhanced_stats.devices.top_device_clicks} clicks
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      textAlign: 'center', 
                      p: { xs: 2, sm: 3 },
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  >
                    <BotIcon 
                      sx={{ 
                        fontSize: { xs: 32, sm: 40 }, 
                        color: 'primary.main', 
                        mb: 1 
                      }} 
                    />
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {analytics.enhanced_stats.bot_detection.bot_percentage}%
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontWeight: 500, mb: 1 }}
                    >
                      Bot Traffic
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                    >
                      {analytics.enhanced_stats.bot_detection.bot_clicks} bot clicks
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Box 
                sx={{ 
                  p: { xs: 2, sm: 3 },
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  borderRadius: 2
                }}
              >
                <Typography 
                  variant="body1" 
                  sx={{ mb: 1, fontWeight: 500 }}
                >
                  {analytics.enhanced_stats.message}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                >
                  Enhanced analytics includes geographic data, device insights, time patterns, UTM tracking, and bot detection.
                </Typography>
              </Box>
            )}
          </Paper>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Total Clicks
                  </Typography>
                  <Typography 
                    variant={isMobile ? "h5" : "h4"}
                    sx={{ fontWeight: 600 }}
                  >
                    {analytics.statistics.total_clicks.toLocaleString()}
                  </Typography>
                </Box>
                <Box 
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'primary.light',
                    color: 'primary.main'
                  }}
                >
                  <Mouse sx={{ fontSize: { xs: 32, sm: 40 } }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:hover': {
                borderColor: 'success.main',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Today's Clicks
                  </Typography>
                  <Typography 
                    variant={isMobile ? "h5" : "h4"}
                    sx={{ fontWeight: 600 }}
                  >
                    {(analytics.statistics.daily_clicks.find(
                      day => day.date === new Date().toISOString().split('T')[0]
                    )?.clicks || 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box 
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.light',
                    color: 'success.main'
                  }}
                >
                  <TrendingUp sx={{ fontSize: { xs: 32, sm: 40 } }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:hover': {
                borderColor: 'info.main',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between"
                spacing={2}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Top Referrer
                  </Typography>
                  <Typography 
                    variant={isMobile ? "body1" : "h6"} 
                    noWrap
                    sx={{ fontWeight: 600 }}
                  >
                    {analytics.statistics.top_referrers[0]?.referrer || 'Direct'}
                  </Typography>
                </Box>
                <Box 
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'info.light',
                    color: 'info.main'
                  }}
                >
                  <Public sx={{ fontSize: { xs: 32, sm: 40 } }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:hover': {
                borderColor: 'warning.main',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack 
                direction="row" 
                alignItems="center" 
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Top Browser
                  </Typography>
                  <Typography 
                    variant={isMobile ? "body1" : "h6"}
                    sx={{ fontWeight: 600 }}
                  >
                    {analytics.statistics.browser_stats[0]?.browser || 'Unknown'}
                  </Typography>
                </Box>
                <Box 
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'warning.light',
                    color: 'warning.main'
                  }}
                >
                  <Computer sx={{ fontSize: { xs: 32, sm: 40 } }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        {/* Top Referrers */}
        <Grid item xs={12} lg={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              gutterBottom 
              sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}
            >
              Top Referrers
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {analytics.statistics.top_referrers.length > 0 ? (
              <Stack spacing={2.5}>
                {analytics.statistics.top_referrers.slice(0, 5).map((referrer, index) => (
                  <Box key={index}>
                    <Stack 
                      direction="row" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography 
                        variant="body2" 
                        noWrap 
                        sx={{ 
                          flex: 1, 
                          mr: 2, 
                          fontWeight: 500,
                          color: 'text.primary'
                        }}
                      >
                        {referrer.referrer || 'Direct'}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        {referrer.clicks.toLocaleString()}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}
              >
                <Typography variant="body1">
                  No referrer data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Browser Stats */}
        <Grid item xs={12} lg={6}>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              gutterBottom 
              sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}
            >
              Browser Statistics
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {analytics.statistics.browser_stats.length > 0 ? (
              <Stack spacing={2.5}>
                {analytics.statistics.browser_stats.slice(0, 5).map((browser, index) => (
                  <Box key={index}>
                    <Stack 
                      direction="row" 
                      justifyContent="space-between" 
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Computer 
                          sx={{ 
                            fontSize: 16, 
                            color: getBrowserColor(browser.browser) 
                          }} 
                        />
                        <Typography 
                          variant="body2"
                          sx={{ 
                            fontWeight: 500,
                            color: 'text.primary'
                          }}
                        >
                          {browser.browser}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        {browser.clicks.toLocaleString()}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}
              >
                <Typography variant="body1">
                  No browser data available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          gutterBottom 
          sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}
        >
          Date Filters
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={{ xs: 2, sm: 2 }}
          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        >
          <TextField
            label="From Date"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              flex: { xs: 1, sm: 1 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
          
          <TextField
            label="To Date"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              flex: { xs: 1, sm: 1 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={1}
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={isLoading}
              size={isMobile ? "small" : "medium"}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                minWidth: { xs: 'auto', sm: 100 }
              }}
            >
              Apply
            </Button>
            
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              disabled={isLoading}
              size={isMobile ? "small" : "medium"}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: { xs: 'auto', sm: 100 }
              }}
            >
              Clear
            </Button>
            
            <Button
              variant="text"
              startIcon={<Refresh />}
              onClick={() => fetchAnalytics()}
              disabled={isLoading}
              size={isMobile ? "small" : "medium"}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: { xs: 'auto', sm: 100 }
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Click Details */}
      <Paper 
        elevation={0}
        sx={{ 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            Click Details
          </Typography>
        </Box>
        
        {isMobile ? (
          // Mobile Card View
          <Box sx={{ p: 2 }}>
            {clickDetails.length > 0 ? (
              <Stack spacing={2}>
                {clickDetails.map((click) => (
                  <Card 
                    key={click.id}
                    elevation={0}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Date & Time
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {new Date(click.clicked_at).toLocaleString()}
                          </Typography>
                        </Stack>
                        
                        <Divider />
                        
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            IP Address
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                            {click.ip_address}
                          </Typography>
                        </Stack>
                        
                        <Divider />
                        
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Browser
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Computer 
                              fontSize="small" 
                              sx={{ color: getBrowserColor(formatUserAgent(click.user_agent)) }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {formatUserAgent(click.user_agent)}
                            </Typography>
                          </Box>
                        </Stack>
                        
                        <Divider />
                        
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Referrer
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={click.referrer || 'Direct'}
                          >
                            {click.referrer || 'Direct'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No click details available
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          // Desktop Table View
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Date & Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>IP Address</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Browser</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Referrer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clickDetails.map((click) => (
                  <TableRow 
                    key={click.id}
                    sx={{
                      '&:hover': {
                        bgcolor: 'grey.50'
                      },
                      '&:last-child td': {
                        borderBottom: 0
                      }
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(click.clicked_at).toLocaleString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontWeight: 500 }}>
                        {click.ip_address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Computer 
                          fontSize="small" 
                          sx={{ color: getBrowserColor(formatUserAgent(click.user_agent)) }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatUserAgent(click.user_agent)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight: 500
                        }}
                        title={click.referrer || 'Direct'}
                      >
                        {click.referrer || 'Direct'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <TablePagination
            component="div"
            count={analytics.statistics.total_clicks}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              '& .MuiTablePagination-toolbar': {
                px: { xs: 2, sm: 3 }
              }
            }}
          />
        </Box>
      </Paper>

      {/* Success/Error Messages */}
      {(success || error) && (
        <Alert
          severity={error ? 'error' : 'success'}
          onClose={() => { setError(''); setSuccess(''); }}
          sx={{ 
            mt: { xs: 2, sm: 3 },
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}
        >
          {error || success}
        </Alert>
      )}
    </Container>
  );
};

export default Analytics;